import React, { Context, createContext, FC, ReactElement, useState } from 'react'
import { ReactState } from '../../Common/Contexts'
import { useStateAsync } from '../../Common/Hooks/useStateAsync'
import { ReactStateAsync } from '../../Common/Types/Types'
import { DEFAULT_STATUS, EntityCreateStatuses, SelfProvisioningViews } from '../Constants/Constants'
import {
    EntityCreateStatus,
    SelfProvisioningInitalDataFormState,
    SelfProvisioningUser
} from '../../../NonReact/common/Types/SelfProvisioning/SelfProvisioningTypes'
import { deleteTelSysAndPartitions } from '../../../NonReact/common/API/EntityAPIClient'

const ERROR_MESSAGE: string = 'Self Provisioning Context Not Initialised'
const NON_INITIALISED_FUNCITON = (): void => {
    // eslint-disable-next-line
    console.debug(ERROR_MESSAGE)
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const NON_INITIALISED_STATE: ReactState<Nullable<any>> = [null, NON_INITIALISED_FUNCITON]

export type SelfProvisioningContextType = {
    cmeState: ReactState<Nullable<string>>
    deleteableTelSysState: ReactState<Nullable<number>>
    deleteablePartitionState: ReactState<Nullable<number>>
    telSysState: ReactState<Nullable<number>>
    telSysStatus: ReactState<Nullable<EntityCreateStatus>>
    partitionState: ReactState<Nullable<number>>
    partitionStatus: ReactStateAsync<Nullable<EntityCreateStatus>>
    viewState: ReactState<SelfProvisioningViews>
    deviceCountState: ReactState<Nullable<number>>
    usersState: ReactState<Nullable<SelfProvisioningUser[]>>
    formState: ReactState<Nullable<SelfProvisioningInitalDataFormState>>
    nextStep: () => void
    previousStep: () => void
    reset: () => void
}

type Props = {
    deleteableTelSysState: ReactState<Nullable<number>>
    deleteablePartitionState: ReactState<Nullable<number>>
    children: ReactElement
}

/**
 * Contains the state for the self provisioning wizard.
 *
 * @param children The children of the context.
 * @param deleteableTelSysState The state of the tel sys to be deleted.
 * @param deleteablePartitionState The state of the partition to be deleted.
 */
export const SelfProvisioningContextProvider: FC<Props> = ({
    deleteableTelSysState,
    deleteablePartitionState,
    children
}): ReactElement => {
    const cmeState: ReactState<Nullable<string>> = useState<Nullable<string>>()
    const telSysState: ReactState<Nullable<number>> = useState<Nullable<number>>()
    const telSysStatus: ReactState<Nullable<EntityCreateStatus>> = useState<Nullable<EntityCreateStatus>>(
        EntityCreateStatuses.WAITING
    )
    const partitionState: ReactState<Nullable<number>> = useState<Nullable<number>>()
    const partitionStatus: ReactStateAsync<Nullable<EntityCreateStatus>> = useStateAsync<Nullable<EntityCreateStatus>>(
        EntityCreateStatuses.WAITING
    )
    const viewState: ReactState<SelfProvisioningViews> = useState<SelfProvisioningViews>(SelfProvisioningViews.WELCOME)
    const deviceCountState: ReactState<Nullable<number>> = useState<Nullable<number>>()
    const usersState: ReactState<Nullable<SelfProvisioningUser[]>> = useState<Nullable<SelfProvisioningUser[]>>()
    const formState: ReactState<Nullable<SelfProvisioningInitalDataFormState>> =
        useState<Nullable<SelfProvisioningInitalDataFormState>>()

    const [deleteableTelSys, setDeleteableTelSys]: ReactState<Nullable<number>> = deleteableTelSysState
    const [deleteablePartition, setDeleteablePartition]: ReactState<Nullable<number>> = deleteablePartitionState
    const [, setTelSysStatus]: ReactState<Nullable<EntityCreateStatus>> = telSysStatus
    const [, setPartitionStatus]: ReactStateAsync<Nullable<EntityCreateStatus>> = partitionStatus
    const [, setView]: ReactState<SelfProvisioningViews> = viewState

    // Go to the new next view
    const nextStep = (): void => setView((step: number): number => step + 1)

    // Go to the previous view
    const previousStep = (): void => setView((step: number): number => step - 1)

    // After a failed state reset back to beginning
    const reset = (): void => {
        // Delete existing tel sys and partitions.
        deleteTelSysAndPartitions(deleteableTelSys, deleteablePartition)

        // Set statuses back to default.
        setTelSysStatus(DEFAULT_STATUS)
        setPartitionStatus(DEFAULT_STATUS)

        // Remove the deleteable tel sys and partition.
        setDeleteableTelSys(undefined)
        setDeleteablePartition(undefined)

        // Return the data entry view.
        setView(1)
    }

    return (
        <SelfProvisioningContext.Provider
            value={{
                cmeState,
                deleteableTelSysState,
                deleteablePartitionState,
                telSysState,
                telSysStatus,
                partitionState,
                partitionStatus,
                viewState,
                deviceCountState,
                usersState,
                formState,
                nextStep,
                previousStep,
                reset
            }}
        >
            {children}
        </SelfProvisioningContext.Provider>
    )
}

export const SelfProvisioningContext: Context<SelfProvisioningContextType> = createContext<SelfProvisioningContextType>(
    {
        cmeState: NON_INITIALISED_STATE as ReactState<Nullable<string>>,
        deleteableTelSysState: NON_INITIALISED_STATE as ReactState<Nullable<number>>,
        deleteablePartitionState: NON_INITIALISED_STATE as ReactState<Nullable<number>>,
        telSysState: NON_INITIALISED_STATE as ReactState<Nullable<number>>,
        telSysStatus: NON_INITIALISED_STATE as ReactState<Nullable<EntityCreateStatus>>,
        partitionState: NON_INITIALISED_STATE as ReactState<Nullable<number>>,
        partitionStatus: [
            ...(NON_INITIALISED_STATE as ReactState<Nullable<EntityCreateStatus>>),
            (): Promise<EntityCreateStatus> => {
                // eslint-disable-next-line
                console.debug(ERROR_MESSAGE)
                return Promise.resolve(EntityCreateStatuses.WAITING)
            }
        ],
        viewState: NON_INITIALISED_STATE as ReactState<SelfProvisioningViews>,
        deviceCountState: NON_INITIALISED_STATE as ReactState<Nullable<number>>,
        usersState: NON_INITIALISED_STATE as ReactState<Nullable<SelfProvisioningUser[]>>,
        formState: NON_INITIALISED_STATE as ReactState<Nullable<SelfProvisioningInitalDataFormState>>,
        nextStep: NON_INITIALISED_FUNCITON,
        previousStep: NON_INITIALISED_FUNCITON,
        reset: NON_INITIALISED_FUNCITON
    }
)
